import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const SYSROLE_OPTIONS = [
  { value: 'teacher', label: '교사' },
  { value: 'foreign', label: '외부' },
  { value: 'staff', label: '행정' },
  { value: 'admin', label: '관리' },
];

function emptyForm() {
  return {
    teachid: '',
    name: '',
    email: '',
    password: '',
    phone: '',
    specialty: '',
    haddress: '',
    birthyear: '',
    grade: '',
    joindate: '',
    sysrole: 'teacher',
  };
}

function sysroleLabel(v) {
  return SYSROLE_OPTIONS.find((o) => o.value === v)?.label || v;
}

export default function TeacherManagement() {
  const [rows, setRows] = useState([]);
  const [locationFilter, setLocationFilter] = useState('internal');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState('');

  const loadRows = useCallback(async () => {
    const params = {};
    if (locationFilter !== '') params.location = locationFilter;
    const res = await api.get('/admin/teacher-management', { params });
    setRows(res.data);
  }, [locationFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
    setMessage('');
  };

  const openEdit = (row) => {
    setEditingId(row.teachid);
    setForm({
      teachid: row.teachid,
      name: row.name,
      email: row.email,
      password: '',
      phone: row.phone || '',
      specialty: row.specialty || '',
      haddress: row.haddress || '',
      birthyear: row.birthyear || '',
      grade: row.grade ?? '',
      joindate: row.joindate || '',
      sysrole: row.sysrole,
    });
    setFormOpen(true);
    setMessage('');
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!editingId && !form.password.trim()) {
      setMessage('초기 비밀번호를 입력하세요.');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/admin/teacher-management/${editingId}`, {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          specialty: form.specialty || null,
          haddress: form.haddress || null,
          birthyear: form.birthyear ? Number(form.birthyear) : null,
          grade: form.grade === '' ? null : Number(form.grade),
          joindate: form.joindate || null,
          sysrole: form.sysrole,
        });
      } else {
        await api.post('/admin/teacher-management', {
          teachid: form.teachid,
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone || null,
          specialty: form.specialty || null,
          haddress: form.haddress || null,
          birthyear: form.birthyear ? Number(form.birthyear) : null,
          grade: form.grade === '' ? null : Number(form.grade),
          joindate: form.joindate || null,
          sysrole: form.sysrole,
        });
      }
      closeForm();
      await loadRows();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === 'E' ? 'R' : 'E';
    const verb = next === 'R' ? '퇴직' : '재직 복귀';
    if (!window.confirm(`${row.name} 교사를 ${verb} 처리하시겠습니까?`)) return;
    await api.put(`/admin/teacher-management/${row.teachid}/status`, { status: next });
    await loadRows();
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>교사관리</h2>
      </div>

      <div className="card">
        <div className="board-header">
          <h3>교사 목록</h3>
          {!formOpen && (
            <button className="btn-primary btn-inline" onClick={openCreate}>
              + 신입교사 등록
            </button>
          )}
        </div>

        <div className="form-row">
          <div className="form-field form-field-narrow">
            <label>구분</label>
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
              <option value="internal">원내교사</option>
              <option value="external">원외교사</option>
            </select>
          </div>
        </div>

        {formOpen && (
          <form className="board-write-form" onSubmit={submitForm}>
            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>교사ID</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.teachid}
                  disabled={!!editingId}
                  onChange={(e) => updateField('teachid', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>이름</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>이메일(로그인ID)</label>
                <input
                  className="field-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
              </div>
              {!editingId && (
                <div className="form-field form-field-narrow">
                  <label>초기 비밀번호</label>
                  <input
                    className="field-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => updateField('password', e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>전화번호</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>담당</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.specialty}
                  onChange={(e) => updateField('specialty', e.target.value)}
                  placeholder="예: 기체, 엔진"
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>담당 학년</label>
                <select value={form.grade} onChange={(e) => updateField('grade', e.target.value)}>
                  <option value="">-- 없음 --</option>
                  <option value={1}>1학년</option>
                  <option value={2}>2학년</option>
                  <option value={3}>3학년</option>
                </select>
              </div>
              <div className="form-field form-field-narrow">
                <label>권한</label>
                <select value={form.sysrole} onChange={(e) => updateField('sysrole', e.target.value)}>
                  {SYSROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field form-field-narrow">
                <label>출생년도</label>
                <input
                  className="field-input"
                  type="number"
                  value={form.birthyear}
                  onChange={(e) => updateField('birthyear', e.target.value)}
                />
              </div>
              <div className="form-field form-field-narrow">
                <label>입사일</label>
                <input
                  className="field-input"
                  type="date"
                  value={form.joindate}
                  onChange={(e) => updateField('joindate', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>주소</label>
                <input
                  className="field-input"
                  type="text"
                  value={form.haddress}
                  onChange={(e) => updateField('haddress', e.target.value)}
                />
              </div>
            </div>

            {message && <div className="form-message">{message}</div>}

            <div className="comment-actions">
              <button className="btn-primary btn-inline" type="submit">
                {editingId ? '수정 저장' : '등록'}
              </button>
              <button className="btn-ghost" type="button" onClick={closeForm}>취소</button>
            </div>
          </form>
        )}

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>상태</th>
                <th>교사ID</th>
                <th>이름</th>
                <th>이메일</th>
                <th>전화번호</th>
                <th>담당</th>
                <th>학년</th>
                <th>권한</th>
                <th>입사일</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.teachid}>
                  <td>{r.status === 'R' ? '퇴직' : '재직'}</td>
                  <td>{r.teachid}</td>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.phone || ''}</td>
                  <td>{r.specialty || ''}</td>
                  <td>{r.grade ? `${r.grade}학년` : ''}</td>
                  <td>{sysroleLabel(r.sysrole)}</td>
                  <td>{r.joindate || ''}</td>
                  <td className="board-title-cell">
                    <button className="btn-link" onClick={() => openEdit(r)}>수정</button>
                    {' '}
                    <button className="btn-link" onClick={() => toggleStatus(r)}>
                      {r.status === 'R' ? '재직 복귀' : '퇴직 처리'}
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan="10" className="empty-row">등록된 교사가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
